import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApi } from '../../Services/apiService';
import {
  AdminStatCard,
  AdminSearch,
  AdminBadge,
  statusToBadge,
  AdminModal,
  AdminModalFooter,
  AdminPagination,
} from '../components/AdminUi';
import Input from '../../Components/Input/Input';
import Button from '../../Components/Button/Button';
import {
  getLandlordStatusLabel,
  getLandlordStatusBadgeVariant,
  normalizeLandlordStatus,
  LANDLORD_STATUS,
} from '../../constants/landlordStatus';

const defaultStats = {
  total_landlords: 0,
  active_accounts: 0,
  subscription_payments_count: 0,
  subscription_amount_collected: 0,
};

const defaultPagination = {
  currentPage: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
};

const getInitials = (name) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();

function normalizePagination(pag, stats, fallbackPageSize) {
  const pageSize = pag?.pageSize ?? fallbackPageSize ?? 10;
  const totalItems = pag?.totalItems ?? stats?.total_landlords ?? 0;
  const totalPages = pag?.totalPages ?? Math.max(1, Math.ceil(totalItems / pageSize));
  return {
    currentPage: pag?.currentPage ?? 1,
    pageSize,
    totalPages,
    totalItems,
  };
}

function formatTimestamp(timestamp) {
  if (timestamp == null || timestamp === '') return '—';
  const date = new Date(Number(timestamp));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatCurrency(amount) {
  if (amount == null || amount === '') return '—';
  return `$${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function getPlanName(landlord) {
  return landlord?.current_subscription?.plan?.name || landlord?.plan || null;
}

export default function LandlordsPage() {
  const navigate = useNavigate();
  const [landlordList, setLandlordList] = useState([]);
  const [stats, setStats] = useState(defaultStats);
  const [pagination, setPagination] = useState(defaultPagination);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  const searchTimeoutRef = useRef(null);
  const previousDebouncedSearchRef = useRef('');

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      setIsStatsLoading(true);
      try {
        const response = await getApi('/admin/landlords/stats');
        if (cancelled) return;

        if (response?.statusCode === 200 && response?.data) {
          setStats({ ...defaultStats, ...response.data });
        } else {
          toast.error(response?.message || 'Failed to load landlord stats');
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load landlord stats';
          toast.error(message);
        }
      } finally {
        if (!cancelled) {
          setIsStatsLoading(false);
        }
      }
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchLandlords = async () => {
      setIsLoading(true);
      try {
        const params = {
          page: pagination.currentPage,
          limit: pagination.pageSize,
        };

        if (debouncedSearch.trim()) {
          params.search = debouncedSearch.trim();
        }

        const response = await getApi('/admin/landlords', { params });
        if (cancelled) return;

        if (response?.statusCode === 200 && response?.data) {
          setLandlordList(response.data.landlords ?? []);
          setPagination((prev) =>
            normalizePagination(response.data.pagination, null, prev.pageSize)
          );
        } else {
          toast.error(response?.message || 'Failed to load landlords');
          setLandlordList([]);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load landlords';
          toast.error(message);
          setLandlordList([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchLandlords();
    return () => {
      cancelled = true;
    };
  }, [pagination.currentPage, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    if (debouncedSearch !== previousDebouncedSearchRef.current && pagination.currentPage !== 1) {
      previousDebouncedSearchRef.current = debouncedSearch;
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      return;
    }
    previousDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch, pagination.currentPage]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const formatStatValue = (value) =>
    isStatsLoading ? '—' : Number(value ?? 0).toLocaleString();

  const closeModal = () => {
    setViewMode(null);
    setSelected(null);
    setEditData(null);
    setIsDetailLoading(false);
  };

  const openView = async (landlord) => {
    setViewMode('view');
    setSelected(null);
    setIsDetailLoading(true);

    try {
      const response = await getApi(`/admin/landlords/${landlord.id}`);
      if (response?.statusCode === 200 && response?.data) {
        setSelected(response.data);
      } else {
        toast.error(response?.message || 'Failed to load landlord details');
        setViewMode(null);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to load landlord details';
      toast.error(message);
      setViewMode(null);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const openEdit = (landlord) => {
    const source = landlord || selected;
    if (!source) return;

    setSelected(source);
    setEditData({
      id: source.id,
      name: source.name ?? '',
      email: source.email ?? '',
      address: source.address ?? '',
      plan: getPlanName(source) ?? '',
      status: normalizeLandlordStatus(source.status) ?? LANDLORD_STATUS.ACTIVE,
    });
    setViewMode('edit');
  };

  const handleSave = () => {
    if (!editData) return;
    setLandlordList((prev) => prev.map((l) => (l.id === editData.id ? { ...editData } : l)));
    setSelected(editData);
    setViewMode('view');
    setEditData(null);
  };

  const renderAvatar = (landlord, size = 36) => {
    if (landlord.profile_image) {
      return (
        <img
          src={landlord.profile_image}
          alt={landlord.name}
          className="admin-avatar admin-avatar--img"
          style={{ width: size, height: size }}
        />
      );
    }
    return (
      <span
        className="admin-avatar"
        style={size !== 36 ? { width: size, height: size, fontSize: size * 0.35 } : undefined}
      >
        {getInitials(landlord.name)}
      </span>
    );
  };

  return (
    <div className="admin-page admin-page--list">
      <div className="dashboard-stats">
        <AdminStatCard
          label="Total Landlords"
          value={formatStatValue(stats.total_landlords)}
          icon="/assets/images/sidebarPropertyIcon.svg"
        />
        <AdminStatCard
          label="Active Accounts"
          value={formatStatValue(stats.active_accounts)}
          icon="/assets/images/sidebardashboardIcon.svg"
        />
        <AdminStatCard
          label="Subscription Payments"
          value={formatStatValue(stats.subscription_payments_count)}
          icon="/assets/images/Stripe.svg"
          subtitle="Recorded purchases"
        />
        <AdminStatCard
          label="Billing Collected"
          value={
            isStatsLoading
              ? '—'
              : `$${Number(stats.subscription_amount_collected ?? 0).toLocaleString()}`
          }
          icon="/assets/images/Stripe.svg"
          subtitle="Completed landlord billing"
        />
      </div>

      <div className="admin-toolbar" style={{ width: '100%' }}>
        <AdminSearch
          fullWidth
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by name, email, or address..."
        />
      </div>

      <div className="admin-table-wrap" style={{ marginBottom: 30 }}>
        <div className="admin-table-scroll">
        <table className="admin-table admin-table--compact">
          <thead>
            <tr>
              <th>Landlord</th>
              <th>Email</th>
              <th>Plan</th>
              <th>Status</th>
              <th className="admin-table-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="admin-table-empty">
                  Loading landlords...
                </td>
              </tr>
            ) : landlordList.length === 0 ? (
              <tr>
                <td colSpan={5} className="admin-table-empty">
                  No landlords match your search.
                </td>
              </tr>
            ) : (
              landlordList.map((landlord) => (
                <tr key={landlord.id}>
                  <td>
                    <div className="admin-user-cell">
                      <span className="admin-avatar">{getInitials(landlord.name)}</span>
                      <span style={{ fontWeight: 600 }}>{landlord.name}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: '13px' }}>{landlord.email}</td>
                  <td>
                    <AdminBadge variant={statusToBadge(landlord.plan)}>
                      {landlord.plan || '—'}
                    </AdminBadge>
                  </td>
                  <td>
                    <AdminBadge variant={getLandlordStatusBadgeVariant(landlord.status)}>
                      {getLandlordStatusLabel(landlord.status)}
                    </AdminBadge>
                  </td>
                  <td className="admin-table-actions">
                    <Button size="small" variant="outline" onClick={() => openView(landlord)}>
                      View
                    </Button>
                    <Button size="small" variant="success" onClick={() => openEdit(landlord)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        {!isLoading && pagination.totalItems > 0 && (
          <AdminPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <AdminModal
        open={viewMode !== null}
        onClose={closeModal}
        title={viewMode === 'view' ? 'Landlord Profile' : 'Edit Landlord'}
        footer={
          viewMode === 'edit' ? (
            <AdminModalFooter onCancel={closeModal} onConfirm={handleSave} confirmLabel="Save Changes" />
          ) : !isDetailLoading && selected ? (
            <AdminModalFooter
              onCancel={closeModal}
              onConfirm={() => openEdit(selected)}
              confirmLabel="Edit Landlord"
            />
          ) : (
            <AdminModalFooter onCancel={closeModal} onConfirm={closeModal} confirmLabel="Close" />
          )
        }
      >
        {viewMode === 'view' && isDetailLoading && (
          <p style={{ margin: 0, color: '#7c7e93' }}>Loading landlord details...</p>
        )}

        {viewMode === 'view' && !isDetailLoading && selected && (
          <>
            <div className="admin-user-cell" style={{ marginBottom: 20 }}>
              {renderAvatar(selected, 48)}
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: '18px' }}>{selected.name}</p>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <AdminBadge variant={getLandlordStatusBadgeVariant(selected.status)}>
                    {getLandlordStatusLabel(selected.status)}
                  </AdminBadge>
                  {getPlanName(selected) && (
                    <AdminBadge variant={statusToBadge(getPlanName(selected))}>
                      {getPlanName(selected)} plan
                    </AdminBadge>
                  )}
                </div>
              </div>
            </div>

            <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#7c7e93', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Contact Information
            </h4>
            <div className="admin-detail-grid" style={{ marginBottom: 24 }}>
              <p style={{ margin: 0 }}><strong>Email:</strong> {selected.email || '—'}</p>
              <p style={{ margin: 0 }}>
                <strong>Member since:</strong> {formatTimestamp(selected.member_since)}
              </p>
            </div>

            <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#7c7e93', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Subscription & Billing
            </h4>
            <div className="admin-detail-grid" style={{ marginBottom: 24 }}>
              <p style={{ margin: 0 }}>
                <strong>Plan:</strong> {getPlanName(selected) || '—'}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Price:</strong>{' '}
                {selected.current_subscription?.plan
                  ? `${formatCurrency(selected.current_subscription.plan.price)}/${selected.current_subscription.plan.interval || 'month'}`
                  : '—'}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Subscription status:</strong>{' '}
                {selected.current_subscription?.status
                  ? selected.current_subscription.status.charAt(0).toUpperCase() +
                    selected.current_subscription.status.slice(1)
                  : '—'}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Started:</strong>{' '}
                {formatTimestamp(selected.current_subscription?.start_date)}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Expires:</strong>{' '}
                {formatTimestamp(selected.current_subscription?.plan_expires_at)}
              </p>
              <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>
                  <strong>Properties:</strong> {selected.properties?.length ?? 0}
                </span>
                <Button
                  size="small"
                  variant="outline"
                  onClick={() => {
                    closeModal();
                    navigate(`/admin/dashboard/landlords/${selected.id}/properties`);
                  }}
                >
                  View
                </Button>
              </p>
              <p style={{ margin: 0 }}>
                <strong>Total revenue:</strong> {formatCurrency(selected.total_revenue)}
              </p>
            </div>

            <h4 style={{ margin: '20px 0 12px', fontSize: '14px', color: '#7c7e93', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Subscription & Payment History
            </h4>
            {!selected.subscription_payment_history?.length ? (
              <p style={{ margin: 0, color: '#7c7e93', fontSize: '13px' }}>No transactions on record.</p>
            ) : (
              selected.subscription_payment_history.map((tx) => (
                <div key={tx.id} className="admin-list-item">
                  <div>
                    <p style={{ margin: 0, fontWeight: 500 }}>{tx.title}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#7c7e93' }}>
                      {formatTimestamp(tx.date)} · {tx.type}
                      {tx.payment_method ? ` · ${tx.payment_method}` : ''}
                    </p>
                    {tx.reference && (
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#a0a3b1' }}>
                        Ref: {tx.reference}
                      </p>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0, fontWeight: 600, color: tx.amount < 0 ? '#c62828' : '#2c2c2c' }}>
                      {tx.amount < 0 ? '-' : ''}
                      {formatCurrency(Math.abs(Number(tx.amount)))}
                    </p>
                    <AdminBadge variant={statusToBadge(tx.status)}>{tx.status}</AdminBadge>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {viewMode === 'edit' && editData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Input
              label="Full name"
              value={editData.name}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            />
            <Input
              label="Email"
              type="email"
              value={editData.email}
              onChange={(e) => setEditData({ ...editData, email: e.target.value })}
            />
            <Input
              label="Address"
              value={editData.address}
              onChange={(e) => setEditData({ ...editData, address: e.target.value })}
            />
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: '14px', fontWeight: 500 }}>Subscription plan</label>
              <select
                className="admin-select"
                style={{ width: '100%' }}
                value={editData.plan || editData.subscription || ''}
                onChange={(e) => setEditData({ ...editData, plan: e.target.value })}
              >
                <option value="Enterprise">Enterprise</option>
                <option value="Premium">Premium</option>
                <option value="Standard">Standard</option>
                <option value="Basic">Basic</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: '14px', fontWeight: 500 }}>Account status</label>
              <select
                className="admin-select"
                style={{ width: '100%' }}
                value={editData.status}
                onChange={(e) => setEditData({ ...editData, status: e.target.value })}
              >
                <option value={LANDLORD_STATUS.ACTIVE}>Active</option>
                <option value={LANDLORD_STATUS.INACTIVE}>Inactive</option>
              </select>
            </div>
          </div>
        )}
      </AdminModal>
    </div>
  );
}
